import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { AnimationWeb } from "../components/animator/AnimationWeb";
import { useAnimation } from "../components/animation/useAnimation";
import { useHighlightsManager } from "../components/animation/useHighlightsManager";
import { useMediaAssetsStorage } from "hooks/useMediaAssetsStorage";
import { LoadingSpinner } from "components/LoadingSpinner";
import styled from "styled-components";

type HeyGeniusPreviewProps = {
  projectId?: string;
  /**
   * When true, render as a fixed full-screen overlay (standalone route).
   * When false (default), fill the parent container so the preview can be
   * embedded below the project toolbar in the detail layout.
   */
  fullScreen?: boolean;
};

export const HeyGeniusPreview: React.FC<HeyGeniusPreviewProps> = ({
  projectId,
  fullScreen = false,
}) => {
  const {
    organizationId,
    highlightsAssetPath,
    isLoadingProject,
    projectData,
    media,
    documentAsset,
    brandKit,
    documentId,
  } = useAnimation(projectId);

  const { storyboardHighlights, handleAddHighlight } = useHighlightsManager({
    highlightsAssetPath,
    documentId,
    brandKit,
  });

  const { isLoadingProjectAssets } = useMediaAssetsStorage({
    organizationId: organizationId,
    projectId,
    enabled: Boolean(projectId),
    invalidateOnMutationSuccess: false,
  });

  // Only check loading states for queries that are actually enabled to avoid infinite loading
  // - isLoadingProject: enabled when Boolean(projectId) ✅
  // - brandKit query in useAnimation: enabled when Boolean(brandKitId) - could be disabled, but we don't get its loading state separately
  // - stepsData query in useAnimation: enabled when Boolean(mediaId) - disabled (no mediaId)
  // - projectAssets query: enabled when enabled && Boolean(projectId) ✅
  // - mediaAssets query: enabled when enabled && Boolean(projectId) && Boolean(mediaId) - disabled (no mediaId), not checked
  const hasRequiredData = Boolean(projectId);
  const isLoading =
    hasRequiredData && (isLoadingProject || isLoadingProjectAssets);

  const pageUrl =
    projectData?.data.tags.find((tag) => tag.tag_key === "web_page_url")
      ?.tag_value || "";
  const pageId = useMemo(() => {
    const pageIdTag = projectData?.data.tags.find(
      (tag) => tag.tag_key === "page_id"
    );
    return pageIdTag ? pageIdTag.tag_value : "";
  }, [projectData?.data.tags]);

  const Container = fullScreen ? FullScreenContainer : EmbeddedContainer;

  if (!projectId || isLoading || !projectData) {
    return (
      <Container>
        <LoadingSpinner loading={true} />
      </Container>
    );
  }

  const brandKitPalette = brandKit?.data?.palette;

  return (
    <Container>
      <AnimationWeb
        pageUrl={pageUrl}
        pageId={pageId}
        language={projectData?.data?.settings?.language || "en-US"}
        viewType="standalone-preview"
        organizationId={organizationId}
        storyboardHighlights={
          storyboardHighlights || {
            document_id: documentId || "",
            section_map: {},
            section_highlights: {},
          }
        }
        projectMedia={media}
        projectDocument={documentAsset}
        brandKitPalette={brandKitPalette}
        projectId={projectId}
        addHighlight={handleAddHighlight}
      />
    </Container>
  );
};

export const HeyGeniusPreviewPage: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>();

  return <HeyGeniusPreview projectId={projectId} fullScreen />;
};

// ─── Styled components ───

const FullScreenContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  margin: 0;
  padding: 0;
  overflow: hidden;
  z-index: 9999;

  /* Override AnimationWeb component's height calculation */
  && > * {
    height: 100vh !important;
  }

  /* Ensure iframe container takes full height */
  #iframe-preview-container,
  #iframe-container {
    height: 100vh !important;
  }
`;

const EmbeddedContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;

  /* Override AnimationWeb's viewport-based height so it fills the slot */
  && > * {
    height: 100% !important;
  }

  #iframe-preview-container,
  #iframe-container {
    height: 100% !important;
  }
`;
